import { useCallback, useEffect, useRef, useState } from 'react'
import agentService from '../../../services/agentService'
import { sessionRouteId } from '../utils/sessionRouting'
import { isEnglishVoice } from '../utils/userLabel'

export function useVoiceInput({ session, busy, setStage, setSession }) {
  const [mode, setMode] = useState('text')
  const [voiceActive, setVoiceActive] = useState(false)
  const [voiceTranscript, setVoiceTranscript] = useState('')
  const [inputText, setInputText] = useState('')
  const [availableVoices, setAvailableVoices] = useState([])
  const [selectedVoiceURI, setSelectedVoiceURI] = useState(() => localStorage.getItem('aria_selected_voice_uri') || '')
  const recognitionRef = useRef(null)
  const transcriptRef = useRef('')
  const speechRef = useRef(null)
  const selectedVoiceURIRef = useRef(selectedVoiceURI)

  useEffect(() => {
    selectedVoiceURIRef.current = selectedVoiceURI
  }, [selectedVoiceURI])

  const speakWithSelectedVoice = useCallback((text, options = {}) => {
    if (!text || !window.speechSynthesis) return undefined
    const { preview = false, onStart } = options
    const synth = window.speechSynthesis

    const doSpeak = () => {
      const utter = new SpeechSynthesisUtterance(text)
      const selectedVoice = (synth.getVoices() || []).find((v) => v.voiceURI === selectedVoiceURIRef.current)
      if (selectedVoice) utter.voice = selectedVoice
      utter.rate = 1
      utter.pitch = 0.95
      utter.onstart = () => {
        setStage('speaking')
        onStart?.()
      }
      utter.onend = () => setStage('idle')
      utter.onerror = () => setStage('idle')
      speechRef.current = utter
      if (!preview) synth.cancel()
      synth.resume()
      const timer = setTimeout(() => synth.speak(utter), 40)
      return () => clearTimeout(timer)
    }

    const voices = synth.getVoices() || []
    if (voices.length > 0) return doSpeak()

    let cleanup = () => {}
    const onVoices = () => {
      cleanup()
      cleanup = doSpeak() || (() => {})
    }
    const fallback = setTimeout(onVoices, 600)
    synth.addEventListener('voiceschanged', onVoices)
    cleanup = () => {
      clearTimeout(fallback)
      synth.removeEventListener('voiceschanged', onVoices)
    }
    return cleanup
  }, [setStage])

  useEffect(() => {
    if (!window.speechSynthesis) return undefined
    const syncVoices = () => {
      const voices = window.speechSynthesis.getVoices() || []
      setAvailableVoices(voices)
      if (!selectedVoiceURI && voices.length > 0) {
        const preferred = voices.find((v) => isEnglishVoice(v)) || voices[0]
        if (preferred?.voiceURI) {
          setSelectedVoiceURI(preferred.voiceURI)
          localStorage.setItem('aria_selected_voice_uri', preferred.voiceURI)
        }
      }
    }
    syncVoices()
    window.speechSynthesis.addEventListener('voiceschanged', syncVoices)
    return () => window.speechSynthesis.removeEventListener('voiceschanged', syncVoices)
  }, [selectedVoiceURI])

  const startVoice = useCallback(() => {
    if (busy) return
    const SpeechRec = window.SpeechRecognition || window.webkitSpeechRecognition
    setMode('voice')
    setVoiceActive(true)
    setStage('listening')
    setVoiceTranscript('')
    transcriptRef.current = ''
    if (!SpeechRec) return

    const recognition = new SpeechRec()
    recognition.continuous = false
    recognition.interimResults = true
    recognition.lang = 'en-US'
    recognition.onresult = (event) => {
      let transcript = ''
      for (const result of event.results) transcript += result[0].transcript || ''
      setVoiceTranscript(transcript)
      transcriptRef.current = transcript
    }
    recognition.onerror = () => setVoiceActive(false)
    recognition.onend = async () => {
      const transcript = transcriptRef.current.trim()
      if (transcript && session) {
        const routeId = sessionRouteId(session)
        if (!routeId) return
        try {
          const data = await agentService.sendVoiceTurn(routeId, transcript, 0)
          if (data?.session) setSession(data.session)
        } catch {
          /* handled by caller */
        }
      }
      setVoiceActive(false)
      setMode('text')
      setStage('idle')
    }
    recognitionRef.current = recognition
    recognition.start()
  }, [busy, session?.id, setSession, setStage])

  const stopVoice = useCallback(() => {
    recognitionRef.current?.stop?.()
    setVoiceActive(false)
    setMode('text')
    setStage('idle')
  }, [setStage])

  useEffect(() => () => {
    recognitionRef.current?.stop?.()
    window.speechSynthesis?.cancel()
  }, [])

  return {
    mode,
    voiceActive,
    voiceTranscript,
    inputText,
    setInputText,
    availableVoices,
    selectedVoiceURI,
    setSelectedVoiceURI,
    speakWithSelectedVoice,
    startVoice,
    stopVoice,
  }
}
